import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Modal from '../Modal/modal';
import auditoriaAplicacoesService, {
  BatchInsumoItem,
} from '../../services/auditoria-aplicacoes';
import Agro1Loader from '../Agro1Loader';
import Agro1Toast from '../Agro1Toast';
import useLocalStore from '../../hooks/use-local-store';
import TabHeader from './TabHeader';
import TabSelecao from './TabSelecao';
import TabSubstituicao from './TabSubstituicao';
import TabTalhoes from './TabTalhoes';
import {
  AssistenteSubstituicaoInsumosProps,
  ResultMap,
  Substituicao,
  TabIndex,
  makeSubstituicao,
} from './types';
import { Aplicacao } from '../../@types';

function deduplicateTalhoesById(
  talhoes: { descricao: string; id: string | number }[],
): { descricao: string; id: string | number }[] {
  return talhoes.filter(
    (talhao, index, self) =>
      self.findIndex((outro) => outro.id === talhao.id) === index,
  );
}

const AssistenteSubstituicaoInsumos: React.FC<
  AssistenteSubstituicaoInsumosProps
> = ({ visible, onHide, itens }) => {
  const insumosUnicosMap = useMemo(
    () =>
      itens
        .filter(
          (item): item is Aplicacao & { type: 'aplicacoes' } =>
            item.type === 'aplicacoes',
        )
        .reduce((result: ResultMap, item) => {
          item.produtos.forEach((produto) => {
            const id = produto.itemId as string | number;

            if (!result[id]) {
              result[id] = {
                id,
                nome: produto.descricao as string,
                talhoes: [],
                unidadesCompativeis: (produto.unidadesCompativeis ?? []).map(
                  (unidade) => ({
                    id: unidade.id,
                    descricao: unidade.descricaoSistema,
                    abreviatura: unidade.abreviatura,
                  }),
                ),
              };
            }

            if (!item.gleba || !item.gleba?.id || !item.gleba?.descricao) {
              return;
            }

            const talhao = {
              descricao: item.gleba.descricao,
              id: item.gleba.id,
            };
            result[id].talhoes.push(talhao);
            result[id].talhoes = deduplicateTalhoesById(result[id].talhoes);
          });

          return result;
        }, {}),
    [itens],
  );

  const insumosUnicos = useMemo(
    () =>
      Object.values(insumosUnicosMap).map((insumo) => ({
        id: String(insumo.id),
        nome: insumo.nome,
        talhoes: insumo.talhoes,
        unidadesCompativeis: insumo.unidadesCompativeis,
      })),
    [insumosUnicosMap],
  );

  const [activeTab, setActiveTab] = useState<TabIndex>(0);
  const [selectedInsumos, setSelectedInsumos] = useState<string[]>([]);
  const [substituicoes, setSubstituicoes] = useState<
    Record<string, Substituicao>
  >({});
  const { value: currentPropriedade } = useLocalStore('currentPropriedade');

  useEffect(() => {
    if (!visible) {
      setSelectedInsumos([]);
      setSubstituicoes({});
      setActiveTab(0);
    }
  }, [visible]);

  const toggleInsumo = useCallback((id: string) => {
    setSelectedInsumos((prev) =>
      prev.includes(id)
        ? prev.filter((insumoId) => insumoId !== id)
        : [...prev, id],
    );
    setSubstituicoes((prev) => ({
      ...prev,
      [id]: prev[id] ?? makeSubstituicao(id),
    }));
  }, []);

  const updateSubstituicao = useCallback(
    (
      id: string,
      field: keyof Omit<Substituicao, 'insumoId' | 'talhoes'>,
      value: string | null,
    ) => {
      setSubstituicoes((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: value },
      }));
    },
    [],
  );

  const toggleTalhao = useCallback((insumoId: string, talhaoId: string) => {
    setSubstituicoes((prev) => {
      const substituicao = prev[insumoId];
      const next = substituicao.talhoes.includes(talhaoId)
        ? substituicao.talhoes.filter(
            (talhaoIdItem) => talhaoIdItem !== talhaoId,
          )
        : [...substituicao.talhoes, talhaoId];
      return { ...prev, [insumoId]: { ...substituicao, talhoes: next } };
    });
  }, []);

  const handleSalvar = useCallback(() => {
    if (!currentPropriedade?.id) {
      Agro1Toast.show({
        severity: 'warn',
        detail: 'Por favor selecione uma propriedade',
        life: 4000,
      });
      return;
    }

    const batch: BatchInsumoItem[] = selectedInsumos
      .map((insumoId) => {
        const substituicao = substituicoes[insumoId];
        if (
          !substituicao?.por ||
          !substituicao?.dose ||
          !substituicao?.unidade
        ) {
          return null;
        }

        const insumo = insumosUnicos.find(
          (insumoItem) => insumoItem.id === insumoId,
        );
        const unidadeCompativel = insumo?.unidadesCompativeis?.find(
          (unidade) => String(unidade.id) === substituicao.unidade,
        );

        const selectedTalhaoIds = substituicao.talhoes;

        const auditoriaIds = itens
          .filter((item) => {
            if (item.type !== 'aplicacoes') return false;
            const hasInsumo = item.produtos.some(
              (produto) => String(produto.itemId) === insumoId,
            );
            const talhaoSelected = selectedTalhaoIds.includes(
              String(item.gleba.id),
            );
            return hasInsumo && talhaoSelected;
          })
          .map((item) => item.id);

        if (auditoriaIds.length === 0) return null;
        return {
          auditoriaIds,
          insumoNew: {
            itemId: Number(substituicao.por),
            itemTipo: 1,
            dose: Number(substituicao.dose),
            unidadeSistema: unidadeCompativel?.descricao ?? '',
          },
          insumoOld: {
            itemId: Number(insumoId),
            itemTipo: 1,
          },
        } as BatchInsumoItem;
      })
      .filter((item): item is BatchInsumoItem => item !== null);

    if (batch.length === 0) return;

    const loaderId = Agro1Loader.show();
    auditoriaAplicacoesService
      .batchInsumos(currentPropriedade.id, batch)
      .then(() => {
        Agro1Toast.show({
          severity: 'success',
          detail: 'Substituições salvas com sucesso!',
          life: 3000,
        });
        onHide();
      })
      .catch((error) => {
        const detail = error?.response?.data ?? 'Erro ao salvar substituições.';
        Agro1Toast.show({
          severity: 'error',
          detail,
          life: 4000,
        });
      })
      .finally(() => {
        Agro1Loader.close(loaderId);
      });
  }, [
    onHide,
    currentPropriedade,
    selectedInsumos,
    substituicoes,
    insumosUnicos,
    itens,
  ]);

  const selectedInsumoObjects = insumosUnicos.filter((insumo) =>
    selectedInsumos.includes(insumo.id),
  );

  const substituicoesCompletas =
    selectedInsumos.length > 0 &&
    selectedInsumos.every(
      (id) =>
        !!substituicoes[id]?.por &&
        !!substituicoes[id]?.dose &&
        !!substituicoes[id]?.unidade,
    );

  const content = (
    <div className="assistente-substituicao-insumos">
      <TabHeader
        activeTab={activeTab}
        selectedInsumos={selectedInsumos}
        substituicoesCompletas={substituicoesCompletas}
        onTabChange={setActiveTab}
      />
      <div className="assistente-tab-content">
        {activeTab === 0 && (
          <TabSelecao
            insumosUnicos={insumosUnicos}
            selectedInsumos={selectedInsumos}
            onToggleInsumo={toggleInsumo}
          />
        )}
        {activeTab === 1 && (
          <TabSubstituicao
            selectedInsumoObjects={selectedInsumoObjects}
            substituicoes={substituicoes}
            insumosUnicosMap={insumosUnicosMap}
            propriedadeId={currentPropriedade?.id}
            onUpdateSubstituicao={updateSubstituicao}
          />
        )}
        {activeTab === 2 && (
          <TabTalhoes
            selectedInsumoObjects={selectedInsumoObjects}
            substituicoes={substituicoes}
            onToggleTalhao={toggleTalhao}
            onSalvar={handleSalvar}
          />
        )}
      </div>
    </div>
  );

  return (
    <Modal
      visible={visible}
      title="Assistente de ajuste de insumos"
      content={content}
      actions={[]}
      onHide={onHide}
      className="assistente-substituicao-insumos-modal"
      dismissableMask={false}
      closable
      position="top"
    />
  );
};

export default React.memo(AssistenteSubstituicaoInsumos);
